import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
    DollarSign, Users, Percent, Check, TrendingUp, RefreshCw, AlertCircle,
    ArrowRight, Sparkles, Menu, Sun, Moon, MousePointer, Eye, Share2
} from 'lucide-react';

// Import the actual NextGenSavingsLadder component
import NextGenSavingsLadder from './NextGenSavingsLadder';

/**
 * EnhancedProfileMobile Component - 2025 Edition
 * Ultra-modern design language with cohesive light/dark modes
 */
const EnhancedProfileMobile = ({
    user,
    isDarkMode,
    toggleTheme,
    statsScrollRef,
    activeCardIndex,
    progressValue,
    scrollToCard,
    paymentData,
    loading,
    error,
    refreshing,
    fetchPaymentData,
    formatPaymentAmount,
    formatDueDate,
    formatActiveSubscriptions,
    getActiveSubscriptionsMessage,
    formatTotalSavings,
    formatShortDate,
    isUserVerified,
    getFallbackAvatarUrl,
    handleShareCircle,
    tierData,
    tiers,
    isCircleDataLoading,
    circleDataError
}) => {
    // State for component
    const [timeOfDay, setTimeOfDay] = useState('day');
    const viewportRef = useRef(null);
    const [animateNextTier, setAnimateNextTier] = useState(false);
    
    // Mouse tracking for 3D effects
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const handleMouseMove = (e) => {
        if (viewportRef.current) {
            const rect = viewportRef.current.getBoundingClientRect();
            setMousePosition({
                x: (e.clientX - rect.left) / rect.width,
                y: (e.clientY - rect.top) / rect.height
            });
        }
    };

    // Determine time of day for adaptive UI
    useEffect(() => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) {
            setTimeOfDay('morning');
        } else if (hour >= 12 && hour < 17) {
            setTimeOfDay('afternoon');
        } else if (hour >= 17 && hour < 20) {
            setTimeOfDay('evening');
        } else {
            setTimeOfDay('night');
        }
    }, []);

    // Animate Next Tier periodically
    useEffect(() => {
        const animationInterval = setInterval(() => {
            setAnimateNextTier(true);
            setTimeout(() => {
                setAnimateNextTier(false);
            }, 3000);
        }, 7000);

        return () => {
            clearInterval(animationInterval);
        };
    }, []);

    // Get time-based color palette
    const getThemeColors = () => {
        if (isDarkMode) {
            return {
                primary: '#818cf8',
                secondary: '#38bdf8',
                accent: '#f43f5e',
                success: '#4ade80',
                warning: '#fbbf24',
                textPrimary: '#f1f5f9',
                textSecondary: '#cbd5e1',
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                surfacePrimary: 'rgba(30, 41, 59, 0.9)',
                surfaceSecondary: 'rgba(15, 23, 42, 0.8)'
            };
        } else {
            return {
                primary: '#4f46e5',
                secondary: '#0ea5e9',
                accent: '#e11d48',
                success: '#10b981',
                warning: '#f59e0b',
                textPrimary: '#0f172a',
                textSecondary: '#334155',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f5f7fa 100%)',
                surfacePrimary: 'rgba(255, 255, 255, 0.9)',
                surfaceSecondary: 'rgba(248, 250, 252, 0.8)'
            };
        }
    };

    const themeColors = getThemeColors();

    // Calculate if max tier is reached
    const isMaxTier = !tierData?.nextTier || (tierData?.membersToNextTier <= 0);

    // Format helpers
    const formatCurrency = (value) => {
        if (value === undefined || value === null) return '0.00';
        return typeof value === 'number' ? value.toFixed(2) : parseFloat(value || 0).toFixed(2);
    };

    // Calculate price difference between current and next tier
    const calculateSavings = () => {
        if (!tierData?.currentTier || !tierData?.nextTier) return 0;
        return tierData.currentTier.price_per_line - tierData.nextTier.price_per_line;
    };

    return (
      <div 
        ref={viewportRef}
        className={`mobile-profile ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
        onMouseMove={handleMouseMove}
      >
        {/* CSS Styles */}
        <style jsx>{`
          .mobile-profile {
            position: relative;
            min-height: 100vh;
            padding: 0 0 4rem 0;
            background: ${themeColors.background};
            color: ${themeColors.textPrimary};
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
          }
          
          .ambient-background {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            opacity: 0.8;
            z-index: -1;
          }
          
          .noise-texture {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E");
            opacity: ${isDarkMode ? '0.15' : '0.07'};
            pointer-events: none;
            z-index: 1;
          }
          
          /* Header & toolbar styles */
          .toolbar {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            padding: 1rem;
            width: 100%;
            box-sizing: border-box;
          }
          
          .theme-toggle {
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background-color: ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)'};
            color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
            border: 1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(226, 232, 240, 0.7)'};
            box-shadow: ${isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.2)' : '0 4px 12px rgba(0, 0, 0, 0.05)'};
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .theme-toggle:hover {
            background-color: ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)'};
          }
          
          /* Profile card */
          .profile-card {
            background: ${isDarkMode ? 
              'linear-gradient(145deg, rgba(30, 41, 59, 0.5), rgba(15, 23, 42, 0.7))' : 
              'linear-gradient(145deg, rgba(248, 250, 252, 0.85), rgba(241, 245, 249, 0.7))'};
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border: 1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgba(226, 232, 240, 0.7)'};
            box-shadow: 0 8px 32px ${isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.08)'};
            padding: 1rem 1rem 1.5rem;
            margin-bottom: 1.5rem;
            position: relative;
            overflow: hidden;
            width: 100%;
            box-sizing: border-box;
            border-radius: 0;
          }
          
          /* Avatar and user info */
          .profile-user {
            display: flex;
            align-items: center;
            margin-bottom: 1.5rem;
            width: 100%;
          }
          
          .avatar-container {
            position: relative;
          }
          
          .avatar {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            border: 2px solid ${themeColors.accent};
            object-fit: cover;
            background-color: #e2e8f0;
          }
          
          .verified-badge {
            position: absolute;
            bottom: 0;
            right: 0;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background-color: ${themeColors.accent};
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid ${isDarkMode ? '#111827' : '#ffffff'};
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }
          
.user-info {
  margin-left: 0.75rem;
  flex: 1;
  text-align: left;  /* Added here */
  display: flex;
  flex-direction: column;
  justify-content: center;
}
          
          .username {
            font-weight: 700;
            font-size: 1.25rem;
            line-height: 1.2;
            color: ${isDarkMode ? '#88abff' : themeColors.primary};
            margin-bottom: 4px;
          }
          
          .email {
            font-size: 0.75rem;
            font-weight: 500;
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.7)' : themeColors.textSecondary};
          }
          
          /* Welcome message */
          .welcome-message {
            margin-bottom: 1.5rem;
            text-align: left;
          }
          
          .welcome-title {
            font-weight: 800;
            font-size: 2.25rem;
            margin-bottom: 0.75rem;
            letter-spacing: -0.02em;
            background: ${isDarkMode ? 
              'linear-gradient(90deg, #88abff, #ff88a8)' : 
              `linear-gradient(90deg, ${themeColors.primary}, ${themeColors.accent})`};
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            color: transparent;
          }
          
          .welcome-subtitle {
            font-size: 0.95rem;
            line-height: 1.5;
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.7)' : themeColors.textSecondary};
            max-width: 95%;
          }
          
          /* Error alert */
          .error-alert {
            border-radius: 14px;
            padding: 12px 16px;
            background: ${isDarkMode ? 'rgba(180, 83, 9, 0.2)' : 'rgba(255, 237, 213, 0.9)'};
            color: ${isDarkMode ? '#fdba74' : '#9a3412'};
            border: 1px solid ${isDarkMode ? 'rgba(253, 186, 116, 0.3)' : 'rgba(154, 52, 18, 0.2)'};
            display: flex;
            align-items: center;
            margin-bottom: 1rem;
          }
          
          .error-icon {
            margin-right: 12px;
            color: ${isDarkMode ? '#fdba74' : '#9a3412'};
          }
          
          .error-message {
            flex: 1;
          }
          
          .error-action {
            background: transparent;
            border: none;
            padding: 8px;
            cursor: pointer;
            color: inherit;
          }
          
          /* Section headers */
          .section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 1rem;
            padding: 0 1rem;
          }
          
          .section-title {
            font-weight: 600;
            font-size: 1rem;
            color: ${themeColors.textPrimary};
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .chip {
            height: 26px;
            padding: 0 10px;
            border-radius: 13px;
            font-size: 0.75rem;
            background: ${isDarkMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(79, 70, 229, 0.1)'};
            color: ${isDarkMode ? themeColors.primary : themeColors.primary};
            border: 1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.3)' : 'rgba(79, 70, 229, 0.2)'};
            font-weight: 500;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
          }
          
          .chip::after {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: linear-gradient(
                -45deg,
                rgba(255, 255, 255, 0) 0%,
                rgba(255, 255, 255, ${isDarkMode ? '0.1' : '0.3'}) 25%,
                rgba(255, 255, 255, ${isDarkMode ? '0.1' : '0.3'}) 50%,
                rgba(255, 255, 255, 0) 100%
            );
            transform: rotate(25deg);
            animation: shine 3s infinite;
          }
          
          @keyframes shine {
            0% { left: -100%; }
            100% { left: 200%; }
          }
          
          /* Stats cards */
          .stats-scroll {
            display: flex;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            gap: 1rem;
            margin: 0 0.5rem;
            padding: 0 0.5rem;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          
          .stats-scroll::-webkit-scrollbar {
            display: none;
          }
          
          .stat-card {
            scroll-snap-align: center;
            min-width: 230px;
            flex-shrink: 0;
            border-radius: 16px;
            background: ${isDarkMode ? 
              'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))' : 
              'linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))'};
            border: 1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(226, 232, 240, 0.7)'};
            box-shadow: ${isDarkMode ?
              '0 8px 20px -4px rgba(2, 6, 23, 0.3), 0 1px 2px rgba(1, 1, 3, 0.1)' : 
              '0 8px 20px -4px rgba(0, 0, 0, 0.07), 0 1px 2px rgba(0, 0, 0, 0.04)'};
            padding: 1.5rem;
            position: relative;
            overflow: hidden;
            transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          }
          
          .stat-card:hover {
            transform: translateY(-5px);
          }
          
          .stat-title {
            font-weight: 600;
            font-size: 0.95rem;
            margin-bottom: 1.25rem;
            display: flex;
            align-items: center;
            gap: 0.375rem;
          }
          
          .payment-title {
            color: ${isDarkMode ? themeColors.accent : themeColors.accent};
          }
          
          .circles-title {
            color: ${isDarkMode ? themeColors.primary : themeColors.primary};
          }
          
          .savings-title {
            color: ${isDarkMode ? themeColors.warning : '#ca8a04'};
          }
          
          .stat-value {
            font-weight: 700;
            font-size: 1.9rem;
            line-height: 1.2;
            margin-bottom: 0.375rem;
            color: ${themeColors.textPrimary};
          }
          
          .stat-subtitle {
            font-size: 0.85rem;
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.6)' : themeColors.textSecondary};
            display: flex;
            align-items: center;
            gap: 0.25rem;
          }
          
          .decorative-circle {
            position: absolute;
            right: -15px;
            bottom: -15px;
            width: 90px;
            height: 90px;
            border-radius: 50%;
            opacity: 0.7;
          }
          
          .payment-circle {
            background: radial-gradient(circle, ${themeColors.accent}20 0%, transparent 70%);
          }
          
          .circles-circle {
            background: radial-gradient(circle, ${themeColors.primary}20 0%, transparent 70%);
          }
          
          .savings-circle {
            background: radial-gradient(circle, rgba(250, 204, 21, 0.2) 0%, transparent 70%);
          }
          
          /* Scroll indicators */
          .scroll-indicators {
            display: flex;
            justify-content: center;
            gap: 8px;
            margin: 1rem 0;
          }
          
          .indicator {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'};
            transition: all 0.3s ease;
            cursor: pointer;
          }
          
          .indicator.active {
            width: 24px;
            border-radius: 4px;
            background-color: ${isDarkMode ? themeColors.primary : themeColors.primary};
          }
          
          /* Pricing Cards */
          .pricing-section {
            margin: 2rem 0;
            padding: 0 1rem;
          }
          
          .pricing-cards {
            display: flex;
            flex-direction: column;
            gap: 1.25rem;
            width: 100%;
          }
          
          .loading-card {
            height: 180px;
            border-radius: 16px;
            background: ${isDarkMode ? 
              'linear-gradient(145deg, rgba(30, 41, 59, 0.5), rgba(15, 23, 42, 0.7))' : 
              'linear-gradient(145deg, rgba(248, 250, 252, 0.85), rgba(241, 245, 249, 0.7))'};
            border: 1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgba(226, 232, 240, 0.7)'};
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
          }
          
          .loading-shimmer {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(
              90deg,
              transparent 0%,
              ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'} 50%,
              transparent 100%
            );
            animation: loading-shimmer 1.5s infinite;
          }
          
          @keyframes loading-shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          
          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
            border-radius: 50%;
            border-top-color: ${isDarkMode ? themeColors.primary : themeColors.primary};
            animation: spin 1s linear infinite;
          }
          
          .error-card {
            border-radius: 16px;
            padding: 1.25rem;
            background: ${isDarkMode ?
              'rgba(254, 226, 226, 0.1)' :
              'rgba(254, 226, 226, 0.5)'};
            border: 1px solid ${isDarkMode ? 'rgba(248, 113, 113, 0.3)' : 'rgba(248, 113, 113, 0.2)'};
            color: ${isDarkMode ? '#fca5a5' : '#b91c1c'};
            display: flex;
            align-items: center;
            gap: 1rem;
          }
          
          .current-pricing {
            border-radius: 16px;
            background: ${isDarkMode ? 
              'linear-gradient(145deg, #1e2a4a, #111827)' : 
              'linear-gradient(145deg, #f1f5f9, #e2e8f0)'};
            border: 1px solid ${isDarkMode ? 'rgba(99, 102, 241, 0.1)' : 'rgba(79, 70, 229, 0.1)'};
            box-shadow: ${isDarkMode ?
              '0 10px 30px -5px rgba(2, 6, 23, 0.35), 0 1px 3px rgba(1, 1, 3, 0.1)' :
              '0 10px 30px -5px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.05)'};
            padding: 1.5rem;
            position: relative;
            overflow: hidden;
          }
          
          .tier-label {
            font-size: 0.9rem;
            font-weight: 700;
            text-transform: uppercase;
            margin-bottom: 1.25rem;
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.85)' : themeColors.primary};
            letter-spacing: 0.05em;
          }
          
          .price-box {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin: 1.25rem 0;
          }
          
          .price-value {
            font-size: 3rem;
            font-weight: 700;
            color: ${isDarkMode ? 'white' : themeColors.textPrimary};
            text-align: center;
          }
          
          .price-unit {
            font-size: 0.9rem;
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.75)' : themeColors.textSecondary};
            text-align: center;
            margin-top: 0.25rem;
          }
          
          .members-count {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.375rem;
            margin-top: 1rem;
            color: ${isDarkMode ? "rgba(255, 255, 255, 0.8)" : themeColors.textSecondary};
            font-size: 0.9rem;
          }
          
          /* IMPROVED: Next tier styling for better light mode contrast */
          .next-pricing {
            border-radius: 16px;
            background: ${isDarkMode ?
              'linear-gradient(135deg, #155e75, #0c4a6e)' :
              'linear-gradient(135deg, #e0f2fe, #bae6fd)'};
            border: 1px solid ${isDarkMode ? 'rgba(56, 189, 248, 0.1)' : 'rgba(14, 165, 233, 0.2)'};
            box-shadow: ${isDarkMode ?
              '0 12px 36px -5px rgba(2, 6, 23, 0.4), 0 1px 3px rgba(1, 1, 3, 0.1)' :
              '0 12px 36px -5px rgba(56, 189, 248, 0.25), 0 1px 3px rgba(56, 189, 248, 0.1)'};
            padding: 1.5rem;
            position: relative;
            overflow: hidden;
          }
          
          .next-pricing .tier-label {
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.95)' : '#0284c7'};
            text-shadow: ${isDarkMode ? '0 1px 2px rgba(0, 0, 0, 0.2)' : 'none'};
          }
          
          .next-pricing .price-value {
            color: ${isDarkMode ? 'white' : '#0284c7'};
            text-shadow: ${isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.25)' : 'none'};
          }
          
          .next-pricing .price-unit {
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.9)' : '#0284c7'};
            opacity: 0.9;
          }
          
          .next-pricing .members-count {
            color: ${isDarkMode ? "rgba(255, 255, 255, 0.9)" : "#0284c7"};
          }
          
          .background-glow {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: ${isDarkMode ?
              'radial-gradient(circle at 50% 50%, rgba(56, 189, 248, 0.35) 0%, rgba(56, 189, 248, 0) 70%)' :
              'radial-gradient(circle at 50% 50%, rgba(14, 165, 233, 0.2) 0%, rgba(14, 165, 233, 0) 70%)'};
            opacity: 0.6;
          }
          
          .sparkle-icon {
            position: absolute;
            top: 1.5rem;
            right: 1.5rem;
            color: ${isDarkMode ? '#38bdf8' : '#0284c7'};
            filter: ${isDarkMode ? 'drop-shadow(0 0 4px rgba(56, 189, 248, 0.5))' : 'none'};
          }
          
          .particles {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            overflow: hidden;
            border-radius: 16px;
          }
          
          .particle {
            position: absolute;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: ${isDarkMode ? '#38bdf8' : '#0284c7'};
            filter: ${isDarkMode ? 'drop-shadow(0 0 4px rgba(56, 189, 248, 0.7))' : 'none'};
            opacity: ${isDarkMode ? 0.7 : 0.5};
          }
          
          @keyframes floatParticle {
            0% { transform: translate(0, 0); }
            25% { transform: translate(10px, 10px); }
            50% { transform: translate(0, 20px); }
            75% { transform: translate(-10px, 10px); }
            100% { transform: translate(0, 0); }
          }
          
          .particle:nth-child(1) {
            top: 20%;
            left: 10%;
            animation: floatParticle 4s linear infinite;
          }
          
          .particle:nth-child(2) {
            top: 35%;
            left: 25%;
            animation: floatParticle 5s linear infinite 0.5s;
          }
          
          .particle:nth-child(3) {
            top: 50%;
            left: 40%;
            animation: floatParticle 6s linear infinite 1s;
          }
          
          .particle:nth-child(4) {
            top: 65%;
            left: 55%;
            animation: floatParticle 7s linear infinite 1.5s;
          }
          
          .particle:nth-child(5) {
            top: 80%;
            left: 70%;
            animation: floatParticle 8s linear infinite 2s;
          }
          
          .savings-badge {
            position: absolute;
            bottom: -10px;
            right: 12px;
            padding: 0.375rem 1.25rem;
            border-radius: 30px;
            background: linear-gradient(90deg, #e11d48, #be123c);
            color: white;
            font-size: 0.85rem;
            font-weight: 700;
            box-shadow: ${isDarkMode ? 
              '0 4px 10px rgba(190, 18, 60, 0.5)' :
              '0 4px 12px rgba(190, 18, 60, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.5) inset'};
            z-index: 2;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }
          
          /* IMPROVED: Need more members section */
          .need-more {
            margin-top: 1.75rem;
            padding: 1.25rem;
            border-radius: 16px;
            background: ${isDarkMode ?
              'rgba(17, 24, 39, 0.7)' :
              'rgba(226, 232, 240, 0.8)'};
            border: 1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.05)'};
            box-shadow: ${isDarkMode ?
              '0 8px 24px -4px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.1)' :
              '0 8px 24px -4px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.05)'};
          }
          
          .need-more-title {
            font-weight: 600;
            font-size: 1rem;
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.95)' : '#334155'};
            margin-bottom: 0.25rem;
          }
          
          .need-more-subtitle {
            font-size: 0.85rem;
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.8)' : '#64748b'};
          }
          


          /* Animation keyframes */
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          .spin-animation {
            animation: spin 1.5s linear infinite;
          }

          /* Dark mode and light mode specific additional styles */
          .dark-mode {
            color-scheme: dark;
          }

          .light-mode {
            color-scheme: light;
          }
        `}</style>

        {/* Ambient background with texture */}
        <div className="ambient-background"></div>
        <div className="noise-texture"></div>

        {/* Modern top toolbar with theme toggle */}
        <div className="toolbar">
          <motion.div
            whileTap={{ scale: 0.9 }}
          >
            <button className="theme-toggle" onClick={toggleTheme}>
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </motion.div>
        </div>

        {/* User Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="profile-card">
            {/* Avatar and username */}
            <div className="profile-user">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="avatar-container"
              >
                <img 
                  src={getFallbackAvatarUrl()} 
                  alt="User avatar" 
                  className="avatar"
                />
                {isUserVerified() && (
                  <div className="verified-badge">
                    <Check size={14} />
                  </div>
                )}
              </motion.div>

              {/* User info */}
              <div className="user-info">
                <div className="username">
                  {user?.name?.split('@')[0] || 'aitest9'}
                </div>
                <div className="email">
                  {user?.email || 'aitest10@joincircledin.com'}
                </div>
              </div>
            </div>

            {/* Welcome message */}
            <div className="welcome-message">
              <div className="welcome-title">
                Welcome back!
              </div>
              <div className="welcome-subtitle">
                Here's a quick overview of your subscription savings and pricing tiers.
              </div>
            </div>

            {/* Error alert */}
            {error && (
              <div className="error-alert">
                <AlertCircle size={20} className="error-icon" />
                <div className="error-message">{error}</div>
                <motion.button 
                  whileTap={{ scale: 0.9 }}
                  className="error-action"
                  onClick={fetchPaymentData}
                  disabled={refreshing}
                >
                  <RefreshCw size={18} className={refreshing ? "spin-animation" : ""} />
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Financial Overview Section */}
        <div>
          <div className="section-header">
            <div className="section-title">
              <DollarSign size={20} />
              Financial Overview
            </div>
            <div className="chip">
              Real-time
            </div>
          </div>

          {/* Stats cards */}
          <div className="stats-scroll" ref={statsScrollRef}>
            {/* Next Payment Card */}
            <motion.div
              className="stat-card"
              whileHover={{ translateY: -5 }}
            >
              <div className="stat-title payment-title">
                <DollarSign size={18} />
                Next Payment
              </div>

              {loading ? (
                <div className="stat-value">--</div>
              ) : (
                <div className="stat-value">
                  {formatPaymentAmount()}
                </div>
              )}

              <div className="stat-subtitle">
                Due {formatDueDate()}
              </div>

              {/* Decorative element */}
              <div className="decorative-circle payment-circle"></div>
            </motion.div>

            {/* Active Circles Card */}
            <motion.div
              className="stat-card"
              whileHover={{ translateY: -5 }}
            >
              <div className="stat-title circles-title">
                <Users size={18} />
                Active Circles
              </div>

              {loading ? (
                <div className="stat-value">--</div>
              ) : (
                <div className="stat-value">
                  {formatActiveSubscriptions()}
                </div>
              )}

              <div className="stat-subtitle">
                {getActiveSubscriptionsMessage()}
              </div>

              {/* Decorative element */}
              <div className="decorative-circle circles-circle"></div>
            </motion.div>

            {/* Total Savings Card */}
            <motion.div
              className="stat-card"
              whileHover={{ translateY: -5 }}
            >
              <div className="stat-title savings-title">
                <Percent size={18} />
                Total Savings
              </div>

              <div className="stat-value">
                {formatTotalSavings()}
              </div>

              <div className="stat-subtitle">
                Since {formatShortDate(user?.joinDate)}
              </div>

              {/* Decorative element */}
              <div className="decorative-circle savings-circle"></div>
            </motion.div>
          </div>

          {/* Scroll indicators */}
          <div className="scroll-indicators">
            {[0, 1, 2].map((index) => (
              <motion.div
                key={`indicator-${index}`}
                className={`indicator ${activeCardIndex === index ? 'active' : ''}`}
                onClick={() => scrollToCard(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.8 }}
              />
            ))}
          </div>
        </div>

        {/* Pricing Optimization Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="pricing-section"
        >
          <div className="section-header">
            <div className="section-title">
              <Sparkles size={22} color={isDarkMode ? themeColors.primary : themeColors.primary} />
              Pricing Optimization
            </div>
            <div className="chip">
              AI Powered
            </div>
          </div>

          {/* Pricing Cards Container with loading and error states */}
          <div className="pricing-cards">
            {isCircleDataLoading ? (
              // Loading state
              <div className="loading-card">
                <div className="loading-shimmer"></div>
                <div className="loading-spinner"></div>
              </div>
            ) : circleDataError && !tierData ? (
              // Error state
              <div className="error-card">
                <AlertCircle size={20} />
                <div>Unable to load pricing data. Please try again later.</div>
              </div>
            ) : !tierData ? (
              // No data state
              <div className="error-card">
                <AlertCircle size={20} />
                <div>No pricing tiers available at this time.</div>
              </div>
            ) : (
              // Data available - show current and next tier cards
              <>
                {/* Current Tier Card */}
                <div className="current-pricing">
                  <div className="tier-label">Current</div>

                  <div className="price-box">
                    <div className="price-value">
                      ${formatCurrency(tierData.currentTier.price_per_line)}
                    </div>
                    <div className="price-unit">/mo per line</div>
                  </div>

                  <div className="members-count">
                    <Users size={18} />
                    <span>{tierData.currentTier.line_count} Members</span>
                  </div>
                </div>

                {/* Next Tier Card - Only show if not at max tier */}
                {!isMaxTier && (
                  <motion.div
                    animate={{
                      scale: animateNextTier ? 1.02 : 1,
                      boxShadow: animateNextTier ? [
                        isDarkMode ? 
                          '0 12px 36px -5px rgba(2, 6, 23, 0.4), 0 1px 3px rgba(1, 1, 3, 0.1)' :
                          '0 12px 36px -5px rgba(56, 189, 248, 0.25), 0 1px 3px rgba(56, 189, 248, 0.1)',
                        isDarkMode ? 
                          '0 20px 40px -5px rgba(56, 189, 248, 0.4), 0 1px 3px rgba(56, 189, 248, 0.2)' : 
                          '0 20px 40px -5px rgba(56, 189, 248, 0.3), 0 1px 3px rgba(56, 189, 248, 0.15)',
                        isDarkMode ? 
                          '0 12px 36px -5px rgba(2, 6, 23, 0.4), 0 1px 3px rgba(1, 1, 3, 0.1)' :
                          '0 12px 36px -5px rgba(56, 189, 248, 0.25), 0 1px 3px rgba(56, 189, 248, 0.1)'
                      ] : (isDarkMode ? 
                        '0 12px 36px -5px rgba(2, 6, 23, 0.4), 0 1px 3px rgba(1, 1, 3, 0.1)' :
                        '0 12px 36px -5px rgba(56, 189, 248, 0.25), 0 1px 3px rgba(56, 189, 248, 0.1)')
                    }}
                    transition={{
                      scale: {
                        duration: 1.2,
                        repeat: animateNextTier ? 1 : 0,
                        repeatType: "reverse",
                        ease: "easeInOut"
                      }
                    }}
                    style={{
                      position: 'relative',
                      borderRadius: '16px',
                      overflow: 'visible'
                    }}
                  >
                    <div className="next-pricing">
                      {/* Background effect */}
                      <div className="background-glow"></div>

                      {/* Content */}
                      <div style={{ position: 'relative', zIndex: 1 }}>
                        <div className="tier-label">Next Tier</div>

                        {/* Sparkle icon */}
                        <motion.div 
                          className="sparkle-icon"
                          animate={{ 
                            rotate: [0, 15, -5, 0],
                            scale: [1, 1.2, 0.9, 1] 
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            repeatType: 'loop'
                          }}
                        >
                          <Sparkles size={22} />
                        </motion.div>

                        <div className="price-box">
                          <div className="price-value">
                            ${formatCurrency(tierData.nextTier.price_per_line)}
                          </div>
                          <div className="price-unit">/mo per line</div>
                        </div>

                        <div className="members-count">
                          <Users size={18} />
                          <span>{tierData.nextTier.line_count} Members</span>
                        </div>
                      </div>

                      {/* Particle effects */}
                      <div className="particles">
                        <div className="particle"></div>
                        <div className="particle"></div>
                        <div className="particle"></div>
                        <div className="particle"></div>
                        <div className="particle"></div>
                      </div>
                    </div>

                    {/* Savings badge - only if there are savings */}
                    {calculateSavings() > 0 && (
                      <motion.div
                        className="savings-badge"
                        initial={{ y: 5, opacity: 0.8 }}
                        animate={{ y: 0, opacity: 1 }}
                      >
                        Save ${formatCurrency(calculateSavings())}/mo
                      </motion.div>
                    )}
                  </motion.div>
                )}

                {/* Need More Members Section - Only if we need more members */}
                {!isMaxTier && tierData.membersToNextTier > 0 && (
                  <div className="need-more">
                    <div className="need-more-title">
                      Need {tierData.membersToNextTier} more member{tierData.membersToNextTier !== 1 ? 's' : ''}
                    </div>
                    <div className="need-more-subtitle">
                      Invite friends to unlock next tier savings
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>

        {/* Complete Savings Ladder - IMPROVED: Better integration using actual NextGenSavingsLadder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="savings-ladder-wrapper"
        >
          {/* Using the actual NextGenSavingsLadder component with proper styling */}
          <NextGenSavingsLadder
            darkMode={isDarkMode}
            handleShareCircle={handleShareCircle}
            forceIsMobile={true} // Force mobile view
          />
        </motion.div>
      </div>
    );
};

export default EnhancedProfileMobile;